import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../../requestOptions';
import type { ExpansionAssignmentRun } from './types';

const validate = (runId: number, subscriptionId: number, assignmentRunId: number) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (!assignmentRunId || assignmentRunId < 1) {
    throw new Error('Valid assignment batch ID is required');
  }
};

type RequestParams = {
  runId: number;
  /**
   * Any subscription of the organization — the API authorises the caller against it and scopes
   * every read by organization, never by this id.
   */
  subscriptionId: number;
  assignmentRunId: number;
};

export type CancelAssignmentResponse = { assignment_run: ExpansionAssignmentRun };

/**
 * Takes a QUEUED batch out of the queue. A batch a worker has already claimed answers 409
 * `assignment_in_progress` — partial writes to another system are worse than finishing.
 */
export const cancelAssignment = async (
  scope: RequestScope,
  { runId, subscriptionId, assignmentRunId }: RequestParams,
  options: WriteRequestOptions
): Promise<ChannelApiResponse<CancelAssignmentResponse>> => {
  try {
    validate(runId, subscriptionId, assignmentRunId);

    const result = await scopedChannelApi(scope).post<CancelAssignmentResponse>(
      `atts/expansion/runs/${runId}/assignments/${assignmentRunId}/cancel`,
      {},
      {
        ...options,
        headers: {
          'x-astur-subscription-id': subscriptionId.toString(),
        },
      }
    );

    return {
      data: result.data,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
